import React, { useEffect, useState } from 'react';
import { 
  Calendar, 
  Modal, 
  Button, 
  Form, 
  Input, 
  Select, 
  DatePicker, 
  TimePicker, 
  message, 
  Tag, 
  Tooltip, 
  Card, 
  Row, 
  Col, 
  Space,
  List,
  Typography,
  Badge,
  Divider,
  Empty,
  Alert,
  Table,
  Spin
} from 'antd';
import { 
  PlusOutlined, 
  SyncOutlined, 
  TeamOutlined, 
  UserOutlined,
  ClockCircleOutlined,
  CalendarOutlined,
  InfoCircleOutlined,
  ExclamationCircleOutlined,
  DeleteOutlined,
  EditOutlined,
  DownloadOutlined,
  LinkOutlined,
  GlobalOutlined,
  SearchOutlined,
  ClearOutlined
} from '@ant-design/icons';
import moment, { Moment } from 'moment';
import apiService, { Activity, User, Team, Lead } from '../services/api';
import { useCognitoAuth } from '../contexts/CognitoAuthContext';
import { useApp } from '../contexts/AppContext';
import { Modal as AntdModal } from 'antd';
import EventModal from './EventModal';
import { getLeadCategoryStyle } from '../utils/leadCategoryColors';

const { Option } = Select;
const { TextArea } = Input;
const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { confirm } = AntdModal;

interface CalendarEvent {
  id: number;
  title: string;
  description?: string;
  activity_type: string;
  due_date: string;
  importance_level: number;
  persons_of_interest?: number[];
  created_by: number;
  created_by_name?: string;
  created_at: string;
  duration_minutes?: number;
  subject?: string;
  outcome?: string;
  next_action?: string;
  notes?: string;
  tags?: string;
  assigned_to?: number;
  lead_id?: number;
  lead_name?: string;
  lead_phone?: string;
}

const importanceLevels = [
  { value: 2, label: 'Média (1 hora)' },
  { value: 3, label: 'Alta (15 min + 1 hora)' }
];

const eventTypes = [
  { value: 'meeting', label: '🤝 Reunião' },
  { value: 'installation', label: '🔧 Instalação' },
  { value: 'follow-up', label: '📋 Follow-up' }
];

const statusOptions = [
  { value: 'pending', label: 'Pendente', color: 'orange' },
  { value: 'in_progress', label: 'Em Progresso', color: 'blue' },
  { value: 'completed', label: 'Concluído', color: 'green' },
  { value: 'cancelled', label: 'Cancelado', color: 'red' }
];

const CalendarComponent: React.FC = () => {
  const { user } = useCognitoAuth();
  const { fetchNotifications, onCalendarDataRefresh } = useApp();
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [selectedTeam, setSelectedTeam] = useState<number | null>(null);
  const [selectedView, setSelectedView] = useState<string>('team'); // 'team', 'personal', or user ID
  const [users, setUsers] = useState<User[]>([]);
  const [leads, setLeads] = useState<Lead[]>([]);
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [viewModalVisible, setViewModalVisible] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Moment | null>(null);
  const [selectedEvents, setSelectedEvents] = useState<any[]>([]);
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [viewLoading, setViewLoading] = useState(false);
  const [syncLoading, setSyncLoading] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editingEvent, setEditingEvent] = useState<CalendarEvent | null>(null);
  const [editForm] = Form.useForm();
  const [editLoading, setEditLoading] = useState(false);
  const [syncModalVisible, setSyncModalVisible] = useState(false);
  const [syncOptions, setSyncOptions] = useState<any>(null);

  // Event Modal state
  const [eventModalVisible, setEventModalVisible] = useState(false);
  const [selectedEventForEdit, setSelectedEventForEdit] = useState<CalendarEvent | null>(null);

  const [eventFilters, setEventFilters] = useState({
    search: '',
    dateRange: null as [Moment, Moment] | null,
    importance: null as number | null,
    type: null as string | null
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  useEffect(() => {
    if (user) {
      fetchTeams();
      fetchUsers();
      fetchLeads();
    }
  }, [user]);

  // Register refresh callback with AppContext
  useEffect(() => {
    if (user) {
      const unregister = onCalendarDataRefresh(() => {
        fetchUsers();
        fetchTeams();
        fetchLeads();
      });
      return unregister;
    }
  }, [user, onCalendarDataRefresh]);

  // Refresh data when component becomes visible (when user navigates back to calendar)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && user) {
        fetchUsers();
        fetchTeams();
        fetchLeads();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [user]);

  useEffect(() => {
    if (user) {
      fetchEvents();
    }
  }, [user, selectedTeam, selectedView]);

  const fetchTeams = async () => {
    if (!user) return;
    try {
      const teamsData = await apiService.getTeams();
      setTeams(teamsData || []);
      
      // Check if user has team management permissions (defined once)
      const hasTeamManagementPermissions = user.role === 'admin' || user.role === 'Admin' || user.role === 'diretor_comercial' || user.role === 'Diretor Comercial' || 
                                          (user.role === 'team_leader' && user.team_id) ||
                                          (user.role === 'gestor_equipa' && user.team_id) ||
                                          (user.role === 'gestor_equipa_junior' && user.team_id);
      
      // Set team selection logic
      if (user.team_id && !selectedTeam && hasTeamManagementPermissions) {
        // User has a team assigned and has management permissions
        setSelectedTeam(user.team_id);
      } else if (!selectedTeam && teamsData && teamsData.length > 0 && hasTeamManagementPermissions) {
        // User doesn't have a team but teams exist and user has management permissions - set first team for admin/director
        if (user.role === 'admin' || user.role === 'Admin' || user.role === 'diretor_comercial' || user.role === 'Diretor Comercial') {
          setSelectedTeam(teamsData[0].id);
        }
      }
      
      // Set default view based on user role and permissions
      if (hasTeamManagementPermissions && user.team_id) {
        // Users with team management permissions can see team calendar by default
        setSelectedView('team');
      } else {
        // Regular users see their personal calendar by default
        setSelectedView('personal');
      }
    } catch (error) {
      message.error('Erro ao carregar equipas.');
      setTeams([]);
    }
  };

  const fetchUsers = async () => {
    if (!user) return;
    try {
      const usersData = await apiService.getUsers();
      setUsers(usersData || []);
    } catch (error) {
      message.error('Erro ao carregar utilizadores.');
      setUsers([]);
    }
  };

  const fetchEvents = async () => {
    if (!user) return;
    setLoading(true);
    setViewLoading(true);
    try {
      let eventsData;
      
      if (selectedView === 'team' && selectedTeam) {
        // Team calendar - get all team members' events
        eventsData = await apiService.getTeamCalendar(selectedTeam);
        eventsData = eventsData.activities || [];
      } else if (selectedView === 'personal') {
        // Personal calendar - only user's own events
        eventsData = await apiService.getActivities(user.id);
      } else if (selectedView !== 'team') {
        // Individual user calendar
        const userId = parseInt(selectedView);
        eventsData = await apiService.getActivities(userId);
      } else {
        // Default to user's own events
        eventsData = await apiService.getActivities(user.id);
      }
      
      // Filter out events without due_date and map to CalendarEvent type
      const validEvents = eventsData
        .filter((event: any) => event.due_date)
        .map((event: any) => ({
          id: event.id,
          title: event.title,
          description: event.description,
          activity_type: event.activity_type,
          due_date: event.due_date!,
          importance_level: event.importance_level || 2,
          persons_of_interest: event.persons_of_interest,
          created_by: event.created_by,
          assigned_to: event.assigned_to,
          created_at: event.created_at,
          duration_minutes: event.duration_minutes,
          subject: event.subject,
          outcome: event.outcome,
          next_action: event.next_action,
          notes: event.notes,
          tags: event.tags,
          lead_id: event.lead_id,
          lead_name: event.lead_name,
          lead_phone: event.lead_phone
        }));
      
      setEvents(validEvents);
    } catch (error) {
      message.error('Erro ao carregar eventos do calendário.');
    }
    setLoading(false);
    setViewLoading(false);
  };

  const fetchLeads = async () => {
    try {
      const data = await apiService.getLeads(undefined, user?.id);
      setLeads(data);
    } catch (error) {
      setLeads([]);
    }
  };

  const getTeamMembers = (teamId: number) => {
    if (!teamId) return [];
    const team = teams.find(t => t.id === teamId);
    return team?.members || [];
  };

  const canViewUserCalendar = (targetUserId: number) => {
    // Users can always view their own calendar
    if (targetUserId === user?.id) {
      return true;
    }
    
    // Admins can view anyone's calendar
    if (user?.role === 'admin' || user?.role === 'Admin') {
      return true;
    }
    
    // Diretor Comercial can view lower rank users' calendars (but not Admin)
    if (user?.role === 'diretor_comercial' || user?.role === 'Diretor Comercial') {
      const targetUser = users.find(u => u.id === targetUserId);
      if (targetUser) {
        // Can view: Team Leader, Gestor de Equipa, Gestor de Equipa Júnior, Vendedor, etc.
        // Cannot view: Admin, Super Admin
        return targetUser.role !== 'admin' && targetUser.role !== 'Admin' && targetUser.role !== 'super_admin';
      }
      return false;
    }
    
    // Team leaders can view their team members' calendars
    if (user?.role === 'team_leader' && user?.team_id) {
      const team = teams.find(t => t.id === user.team_id);
      if (team?.team_leader_id === user.id) {
        const teamMembers = getTeamMembers(user.team_id);
        return teamMembers.some(member => member.id === targetUserId);
      }
    }
    
    // Gestor de Equipa can view their team members' calendars
    if (user?.role === 'gestor_equipa' && user?.team_id) {
      const team = teams.find(t => t.id === user.team_id);
      if (team) {
        const teamMembers = getTeamMembers(user.team_id);
        // Can view gestor_comercial and gestor_equipa_junior members
        const targetMember = teamMembers.find(member => member.id === targetUserId);
        if (targetMember) {
          return targetMember.role === 'gestor_comercial' || targetMember.role === 'gestor_equipa_junior';
        }
      }
    }
    
    // Gestor de Equipa Júnior can view gestor_comercial members
    if (user?.role === 'gestor_equipa_junior' && user?.team_id) {
      const team = teams.find(t => t.id === user.team_id);
      if (team) {
        const teamMembers = getTeamMembers(user.team_id);
        const targetMember = teamMembers.find(member => member.id === targetUserId);
        if (targetMember) {
          return targetMember.role === 'gestor_comercial';
        }
      }
    }
    
    // By default, users can only view their own calendar
    return false;
  };

  const handleDateSelect = (date: Moment) => {
    setSelectedDate(date);
    const dayEvents = events.filter(ev => moment(ev.due_date).isSame(date, 'day'));
    setSelectedEvents(dayEvents);
    setViewModalVisible(true);
  };

  const handleCreateEvent = async (values: any) => {
    if (!user) return;
    setLoading(true);
    try {
      // Validate required fields
      if (!values.event_date || !values.start_time) {
        message.error('Por favor selecione a data e hora do evento!');
        setLoading(false);
        return;
      }

      // Combine date and time from form values
      const eventDateTime = values.event_date
        .hour(values.start_time.hour())
        .minute(values.start_time.minute())
        .second(0);

      // Always include the creator in persons_of_interest for notifications
      const personsOfInterest = [user.id]; // Creator always gets notifications
      if (values.assigned_to && values.assigned_to !== user.id) {
        personsOfInterest.push(values.assigned_to);
      }

      const eventData = {
        title: values.title,
        description: values.description,
        activity_type: values.activity_type,
        due_date: eventDateTime.format('YYYY-MM-DD HH:mm:ss'),
        importance_level: values.importance_level,
        persons_of_interest: personsOfInterest,
        status: 'pending',
        duration_minutes: values.duration_minutes || 60,
        subject: values.subject,
        assigned_to: values.assigned_to || user.id,
        created_by: user.id,
        notes: values.notes,
        tags: values.tags,
        lead_id: values.lead_id
      };

      await apiService.createActivity(eventData);
      message.success('Evento criado com sucesso!');
      setCreateModalVisible(false);
      form.resetFields();
      fetchEvents();
      fetchNotifications();
    } catch (error) {
      message.error('Erro ao criar evento.');
    }
    setLoading(false);
  };

  const handleSync = async () => {
    if (!user) return;
    setSyncLoading(true);
    try {
      const eventIds = events.map(event => event.id);
      const response = await apiService.syncToExternalCalendar(user.id, eventIds);
      
      if (response.success) {
        setSyncOptions(response.sync_options);
        // Prepare all events for the table view
        const allEvents = events.map(event => ({
          id: event.id,
          title: event.title,
          date: moment(event.due_date).format('DD/MM/YYYY HH:mm'),
          importance: event.importance_level,
          type: event.activity_type,
          description: event.description,
          assigned_to: event.assigned_to,
          created_by: event.created_by
        }));
        setSelectedEvents(allEvents);
        setSyncModalVisible(true);
      } else {
        // Handle case when no activities are found
        if (response.message && response.message.includes('Não foram encontrados eventos')) {
          Modal.info({
            title: 'Nenhum Evento Encontrado',
            content: (
              <div>
                <p>Não foram encontrados eventos para sincronizar.</p>
                <p>Para sincronizar o seu calendário:</p>
                <ol>
                  <li>Crie alguns eventos no calendário</li>
                  <li>Ou navegue para um período com eventos existentes</li>
                  <li>Depois tente sincronizar novamente</li>
                </ol>
                <p>Dica: Pode criar eventos clicando no botão "Criar Evento" no cabeçalho do calendário.</p>
              </div>
            ),
            okText: 'Entendi',
          });
        } else {
          message.error(response.message || 'Erro ao gerar opções de sincronização.');
        }
      }
    } catch (error: any) {
      if (error.response) {
        message.error(`Erro ao sincronizar: ${error.response.data?.message || 'Erro desconhecido'}`);
      } else {
        message.error('Erro ao sincronizar com o calendário externo.');
      }
    }
    setSyncLoading(false);
  };

  const handleDownloadIcal = async () => {
    if (!user) return;
    try {
      const blob = await apiService.getIcalCalendar(user.id);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `crm_calendar_${user.username}.ics`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      message.success('Ficheiro iCal descarregado com sucesso!');
    } catch (error) {
      message.error('Erro ao descarregar ficheiro iCal.');
    }
  };

  const handleOpenExternalCalendar = (url: string, type: string) => {
    if (type === 'subscription') {
      // For subscription URLs, show instructions
      Modal.info({
        title: 'Subscrição do Calendário',
        content: (
          <div>
            <p>Para subscrever ao calendário:</p>
            <ol>
              <li>Copie este URL: <code>{url}</code></li>
              <li>Abra a sua aplicação de calendário</li>
              <li>Adicione uma nova subscrição de calendário</li>
              <li>Cole o URL e confirme</li>
            </ol>
            <p>O calendário será atualizado automaticamente quando houver novos eventos.</p>
          </div>
        ),
        width: 600,
      });
    } else if (type === 'data_url') {
      // For Apple Calendar data URLs, create a download link
      const a = document.createElement('a');
      a.href = url;
      a.download = 'event.ics';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      message.success('Evento descarregado para Apple Calendar!');
    } else {
      // For external URLs, open in new tab
      window.open(url, '_blank');
    }
  };

  const handleOpenMultipleEvents = (urls: any[], calendarName: string) => {
    if (urls.length === 0) {
      message.warning('Não há eventos para adicionar.');
      return;
    }

    if (urls.length === 1) {
      // Single event - show preview and open directly
      Modal.confirm({
        title: `Adicionar Evento ao ${calendarName}`,
        content: (
          <div>
            <p><strong>Evento a adicionar:</strong></p>
            <ul>
              <li><strong>{urls[0].title}</strong> - {urls[0].date}</li>
            </ul>
            <p>Clique em "Confirmar" para abrir o calendário.</p>
          </div>
        ),
        okText: 'Confirmar',
        cancelText: 'Cancelar',
        onOk: () => {
          window.open(urls[0].url, '_blank');
          message.success('Evento aberto no calendário!');
        },
      });
    } else {
      // Multiple events - show list and open each one
      Modal.confirm({
        title: `Adicionar ${urls.length} Eventos ao ${calendarName}`,
        content: (
          <div>
            <p><strong>Eventos a adicionar:</strong></p>
            <ul style={{ maxHeight: '200px', overflowY: 'auto' }}>
              {urls.map((url, index) => (
                <li key={index}>
                  <strong>{url.title}</strong> - {url.date}
                </li>
              ))}
            </ul>
            <p>Clique em "Confirmar" para abrir cada evento no calendário.</p>
          </div>
        ),
        okText: 'Confirmar',
        cancelText: 'Cancelar',
        onOk: () => {
          // Open each URL in sequence with a small delay
          urls.forEach((url, index) => {
            setTimeout(() => {
              window.open(url.url, '_blank');
            }, index * 500); // 500ms delay between each
          });
          message.success(`${urls.length} eventos abertos no ${calendarName}!`);
        },
      });
    }
  };

  // Filter and paginate events
  const getFilteredAndPaginatedEvents = () => {
    let filteredEvents = [...selectedEvents];

    // Apply search filter
    if (eventFilters.search) {
      filteredEvents = filteredEvents.filter(event =>
        event.title.toLowerCase().includes(eventFilters.search.toLowerCase()) ||
        event.description?.toLowerCase().includes(eventFilters.search.toLowerCase())
      );
    }

    // Apply date range filter
    if (eventFilters.dateRange && eventFilters.dateRange.length === 2) {
      const [startDate, endDate] = eventFilters.dateRange;
      filteredEvents = filteredEvents.filter(event => {
        const eventDate = moment(event.date, 'DD/MM/YYYY HH:mm');
        return eventDate.isAfter(startDate.startOf('day')) && eventDate.isBefore(endDate.endOf('day'));
      });
    }

    // Apply importance filter
    if (eventFilters.importance) {
      filteredEvents = filteredEvents.filter(event => event.importance === eventFilters.importance);
    }

    // Apply type filter
    if (eventFilters.type) {
      filteredEvents = filteredEvents.filter(event => event.type === eventFilters.type);
    }

    // Apply pagination
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const paginatedEvents = filteredEvents.slice(startIndex, endIndex);

    return {
      events: paginatedEvents,
      total: filteredEvents.length,
      filtered: filteredEvents
    };
  };

  const handleOpenEventInCalendar = (event: any, calendarType: string) => {
    const eventUrls = syncOptions?.[calendarType]?.urls || [];
    const eventUrl = eventUrls.find((url: any) => url.activity_id === event.id);
    
    if (eventUrl) {
      if (calendarType === 'apple_calendar' && eventUrl.type === 'data_url') {
        // Handle Apple Calendar data URL
        const a = document.createElement('a');
        a.href = eventUrl.url;
        a.download = `${event.title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.ics`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        message.success(`Evento "${event.title}" descarregado para Apple Calendar!`);
      } else {
        // Handle other calendar types
        window.open(eventUrl.url, '_blank');
        message.success(`Evento "${event.title}" aberto no ${calendarType === 'google_calendar' ? 'Google Calendar' : calendarType === 'outlook_calendar' ? 'Outlook Calendar' : 'Apple Calendar'}!`);
      }
    } else {
      message.error('URL do evento não encontrada.');
    }
  };

  const dateCellRender = (date: Moment): React.ReactNode => {
    const dayEvents = events.filter(ev => moment(ev.due_date).isSame(date, 'day'));
    
    return (
      <div style={{ minHeight: '80px', padding: '2px' }}>
        {dayEvents.map((ev: CalendarEvent) => {
          const importance = importanceLevels.find((p) => p.value === ev.importance_level);
          const eventType = eventTypes.find((t) => t.value === ev.activity_type);
          const time = moment(ev.due_date).format('HH:mm');
          
          // Get event owner information
          const eventOwner = users.find(u => u.id === ev.assigned_to);
          const isOwnEvent = ev.assigned_to === user?.id;
          
          // Color coding based on event ownership
          let eventColor = '#1890ff'; // default blue
          if (isOwnEvent) {
            eventColor = '#52c41a'; // green for own events
          } else if (eventOwner) {
            eventColor = '#722ed1'; // purple for team member events
          }
          
          // Get lead information
          const lead = leads.find(l => l.id === ev.lead_id);
          
          return (
            <Tooltip
              key={ev.id}
              title={
                <div>
                  <div><strong>{ev.title}</strong></div>
                  <div>{time} - {eventType?.label}</div>
                  {eventOwner && (
                    <div style={{ color: '#666', fontSize: '12px' }}>
                      👤 {eventOwner.username}
                    </div>
                  )}
                  {lead && (
                    <div style={{ color: '#666', fontSize: '12px' }}>
                      🎯 {lead.dif || ''} <span style={getLeadCategoryStyle(lead.lead_category)}>{lead.name}</span> {lead.phone && `(${lead.phone})`}
                    </div>
                  )}
                  {ev.description && <div>{ev.description}</div>}
                </div>
              }
            >
              <div
                style={{
                  background: eventColor,
                  color: 'white',
                  padding: '2px 4px',
                  margin: '1px 0',
                  borderRadius: '3px',
                  fontSize: '10px',
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  border: isOwnEvent ? '1px solid #389e0d' : 'none'
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedEvents([ev]);
                  setViewModalVisible(true);
                }}
              >
                <div style={{ fontWeight: 'bold', fontSize: '9px' }}>{time}</div>
                <div style={{ fontSize: '9px' }}>{ev.title}</div>
                {eventOwner && !isOwnEvent && (
                  <div style={{ fontSize: '8px', opacity: 0.9 }}>
                    {eventOwner.username}
                  </div>
                )}
              </div>
            </Tooltip>
          );
        })}
      </div>
    );
  };

  const getEventDetails = (event: CalendarEvent) => {
    const importance = importanceLevels.find((p) => p.value === event.importance_level);
    const eventType = eventTypes.find((t) => t.value === event.activity_type);
    const assignedUser = users.find(u => u.id === event.assigned_to);
    const creatorUser = users.find(u => u.id === event.created_by);
    const lead = leads.find(l => l.id === event.lead_id);
    const eventDate = moment(event.due_date);
    const endTime = event.duration_minutes ? moment(event.due_date).add(event.duration_minutes, 'minute') : null;
    return (
      <div>
        <Row gutter={16}>
          <Col span={12}>
            <Text strong>Título:</Text>
            <div>{event.title}</div>
          </Col>
          <Col span={12}>
            <Text strong>Tipo de Atividade:</Text>
            <div>{eventType?.label}</div>
          </Col>
        </Row>
        <Divider />
        <Row gutter={16}>
          <Col span={8}>
            <Text strong>Data do Evento:</Text>
            <div><CalendarOutlined /> {eventDate.format('DD/MM/YYYY')}</div>
          </Col>
          <Col span={8}>
            <Text strong>Hora de Início:</Text>
            <div><ClockCircleOutlined /> {eventDate.format('HH:mm')}</div>
          </Col>
          <Col span={8}>
            <Text strong>Hora de Fim:</Text>
            <div><ClockCircleOutlined /> {endTime ? endTime.format('HH:mm') : '-'}</div>
          </Col>
        </Row>
        <Divider />
        <Row gutter={16}>
          <Col span={8}>
            <Text strong>Importância:</Text>
            <div><Tag>{importance?.label}</Tag></div>
          </Col>
          <Col span={8}>
            <Text strong>Atribuído a:</Text>
            <div>{assignedUser ? <><UserOutlined /> {assignedUser.username}</> : '-'}</div>
          </Col>
          <Col span={8}>
            <Text strong>Lead:</Text>
            <div>{event.lead_name ? `${event.lead_name}${event.lead_phone ? ` (${event.lead_phone})` : ''}` : (lead ? `${lead.dif || ''} <span style={getLeadCategoryStyle(lead.lead_category)}>${lead.name}</span>${lead.phone ? ` (${lead.phone})` : ''}` : '-')}</div>
          </Col>
        </Row>
        {event.description && (
          <>
            <Divider />
            <Text strong>Descrição:</Text>
            <div style={{ marginTop: 8 }}>{event.description}</div>
          </>
        )}
        {event.notes && (
          <>
            <Divider />
            <Text strong>Notas:</Text>
            <div style={{ marginTop: 8 }}>{event.notes}</div>
          </>
        )}
        <Divider />
        <Row gutter={16}>
          <Col span={12}>
            <Text strong>Criado por:</Text>
            <div>{event.created_by_name ? <><UserOutlined /> {event.created_by_name}</> : (creatorUser ? <><UserOutlined /> {creatorUser.username}</> : event.created_by)}</div>
          </Col>
          <Col span={12}>
            <Text strong>Criado em:</Text>
            <div>{moment(event.created_at).format('DD/MM/YYYY HH:mm')}</div>
          </Col>
        </Row>
      </div>
    );
  };

  // Delete event handler
  const handleDeleteEvent = (eventId: number) => {
    confirm({
      title: 'Tem a certeza que deseja eliminar este evento?',
      icon: <ExclamationCircleOutlined />,
      content: 'Esta ação não pode ser desfeita.',
      okText: 'Eliminar',
      okType: 'danger',
      cancelText: 'Cancelar',
      onOk: async () => {
        try {
          await apiService.deleteActivity(eventId);
          message.success('Evento eliminado com sucesso!');
          setViewModalVisible(false);
          fetchEvents();
        } catch (error) {
          message.error('Erro ao eliminar evento.');
        }
      },
    });
  };

  const handleEventModalClose = () => {
    setEventModalVisible(false);
    setSelectedEventForEdit(null);
  };

  const handleEventModalSuccess = () => {
    fetchEvents();
  };

  // Function to get upcoming events for the next 3 days
  const getUpcomingEvents = () => {
    const today = moment().startOf('day');
    const dayAfterTomorrow = moment().add(2, 'days').endOf('day');
    
    return events.filter(event => {
      const eventDate = moment(event.due_date);
      return eventDate.isBetween(today, dayAfterTomorrow, 'day', '[]');
    });
  };

  // Function to group events by activity type and day
  const getUpcomingEventsByType = () => {
    const upcomingEvents = getUpcomingEvents();
    const grouped = {
      today: {} as Record<string, CalendarEvent[]>,
      tomorrow: {} as Record<string, CalendarEvent[]>,
      dayAfterTomorrow: {} as Record<string, CalendarEvent[]>
    };

    upcomingEvents.forEach(event => {
      const eventDate = moment(event.due_date);
      const today = moment().startOf('day');
      const tomorrow = moment().add(1, 'day').startOf('day');
      const dayAfterTomorrow = moment().add(2, 'day').startOf('day');

      let targetDay: keyof typeof grouped;
      if (eventDate.isSame(today, 'day')) {
        targetDay = 'today';
      } else if (eventDate.isSame(tomorrow, 'day')) {
        targetDay = 'tomorrow';
      } else {
        targetDay = 'dayAfterTomorrow';
      }

      if (!grouped[targetDay][event.activity_type]) {
        grouped[targetDay][event.activity_type] = [];
      }
      grouped[targetDay][event.activity_type].push(event);
    });

    return grouped;
  };

  // Function to get day label
  const getDayLabel = (day: string) => {
    switch (day) {
      case 'today':
        return 'Hoje';
      case 'tomorrow':
        return 'Amanhã';
      case 'dayAfterTomorrow':
        return 'Depois de amanhã';
      default:
        return day;
    }
  };

  // Function to get activity type label
  const getActivityTypeLabel = (type: string) => {
    const eventType = eventTypes.find(t => t.value === type);
    return eventType ? eventType.label : type;
  };

  const handleOpenCreateEventModal = () => {
    setSelectedEventForEdit(null);
    setEventModalVisible(true);
  };

  const handleEditEvent = (event: CalendarEvent) => {
    setSelectedEventForEdit(event);
    setEventModalVisible(true);
  };

  const getRoleLabel = (role: string) => {
    switch (role) {
      case 'admin':
        return 'Admin';
      case 'diretor_comercial':
        return 'Diretor Comercial';
      case 'vendedor':
        return 'Vendedor';
      case 'tecnico':
        return 'Técnico';
      default:
        return role;
    }
  };

  if (!user) {
    return null;
  }

  return (
    <div>
      {/* Nos próximos dias Section - Now appears first */}
      <Card 
        title={
          <Space>
            <ClockCircleOutlined />
            Nos próximos dias
          </Space>
        }
        style={{ marginBottom: 16 }}
      >
        {(() => {
          const upcomingEventsByType = getUpcomingEventsByType();
          const hasEvents = Object.values(upcomingEventsByType).some(day => 
            Object.values(day).some(events => events.length > 0)
          );

          if (!hasEvents) {
            return (
              <Empty
                description="Nenhum evento nos próximos 3 dias"
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              >
                <Button 
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={handleOpenCreateEventModal}
                >
                  Criar Evento
                </Button>
              </Empty>
            );
          }

          return (
            <Row gutter={[16, 16]}>
              {(['today', 'tomorrow', 'dayAfterTomorrow'] as const).map(day => {
                const dayEvents = upcomingEventsByType[day];
                const dayHasEvents = Object.values(dayEvents).some(events => events.length > 0);

                if (!dayHasEvents) return null;

                return (
                  <Col span={8} key={day}>
                    <Card 
                      title={
                        <Space>
                          <CalendarOutlined />
                          {getDayLabel(day)}
                        </Space>
                      }
                      size="small"
                      style={{ height: '100%' }}
                    >
                      {Object.entries(dayEvents).map(([activityType, events]) => {
                        if (events.length === 0) return null;

                        return (
                          <div key={activityType} style={{ marginBottom: 16 }}>
                            <div style={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              marginBottom: 8,
                              padding: '8px 12px',
                              backgroundColor: '#f8f9fa',
                              borderRadius: '6px',
                              border: '1px solid #e9ecef'
                            }}>
                              <Text strong style={{ fontSize: '14px' }}>
                                {getActivityTypeLabel(activityType)}
                              </Text>
                              <Badge 
                                count={events.length} 
                                style={{ marginLeft: 'auto' }}
                                color="#1890ff"
                              />
                            </div>
                            <List
                              size="small"
                              dataSource={events}
                              renderItem={(event) => (
                                <List.Item
                                  style={{ 
                                    padding: '8px 12px',
                                    border: '1px solid #f0f0f0',
                                    borderRadius: '4px',
                                    marginBottom: '4px',
                                    backgroundColor: 'white',
                                    cursor: 'pointer'
                                  }}
                                  onClick={() => {
                                    setSelectedEvents([event]);
                                    setSelectedDate(moment(event.due_date));
                                    setViewModalVisible(true);
                                  }}
                                >
                                  <div style={{ width: '100%' }}>
                                    <div style={{ 
                                      display: 'flex', 
                                      justifyContent: 'space-between', 
                                      alignItems: 'center',
                                      marginBottom: 4
                                    }}>
                                      <Text strong style={{ fontSize: '12px' }}>
                                        {event.title}
                                      </Text>
                                      <Tag 
                                        color={
                                          event.importance_level === 3 ? 'red' : 
                                          event.importance_level === 2 ? 'orange' : 'green'
                                        }
                                      >
                                        {importanceLevels.find(p => p.value === event.importance_level)?.label}
                                      </Tag>
                                    </div>
                                    <div style={{ 
                                      display: 'flex', 
                                      justifyContent: 'space-between', 
                                      alignItems: 'center'
                                    }}>
                                      <Text type="secondary" style={{ fontSize: '11px' }}>
                                        {moment(event.due_date).format('HH:mm')}
                                      </Text>
                                      {event.lead_id && (
                                        <Text type="secondary" style={{ fontSize: '11px' }}>
                                          👤 {leads.find(l => l.id === event.lead_id)?.name || 'Lead'} {leads.find(l => l.id === event.lead_id)?.phone && `(${leads.find(l => l.id === event.lead_id)?.phone})`}
                                        </Text>
                                      )}
                                    </div>
                                  </div>
                                </List.Item>
                              )}
                            />
                          </div>
                        );
                      })}
                    </Card>
                  </Col>
                );
              })}
            </Row>
          );
        })()}
      </Card>

      <Card 
        title={
          <Space>
            <CalendarOutlined />
            Calendário
          </Space>
        }
        extra={
          <Space>
            <Button 
              icon={<SyncOutlined />} 
              onClick={() => {
                fetchUsers();
                fetchTeams();
                fetchLeads();
              }}
              title="Atualizar dados"
              size="small"
            />
            <Select
              placeholder="Selecionar Vista"
              value={selectedView}
              onChange={setSelectedView}
              style={{ width: 250 }}
            >
              {/* Team Calendar Option - Show if user has team management permissions */}
              {(() => {
                const canViewTeamCalendar = user?.role === 'admin' || user?.role === 'Admin' || user?.role === 'diretor_comercial' || user?.role === 'Diretor Comercial' || 
                                          (user?.role === 'team_leader' && user?.team_id) ||
                                          (user?.role === 'gestor_equipa' && user?.team_id) ||
                                          (user?.role === 'gestor_equipa_junior' && user?.team_id);
                return canViewTeamCalendar && selectedTeam && (
                  <Option key="team" value="team">
                    <TeamOutlined /> Calendário da Equipa {selectedTeam ? `(${teams.find(t => t.id === selectedTeam)?.name})` : ''}
                  </Option>
                );
              })()}
              
              {/* Personal Calendar Option - Always available */}
              <Option key="personal" value="personal">
                <UserOutlined /> Meu Calendário
              </Option>
              
              {/* Individual Team Members - Show only users the current user can view */}
              {(() => {
                const canViewTeamMembers = user?.role === 'admin' || user?.role === 'Admin' || user?.role === 'diretor_comercial' || user?.role === 'Diretor Comercial' || 
                                          (user?.role === 'team_leader' && user?.team_id) ||
                                          (user?.role === 'gestor_equipa' && user?.team_id) ||
                                          (user?.role === 'gestor_equipa_junior' && user?.team_id);
                
                // For admins and directors, show all users
                if (user?.role === 'admin' || user?.role === 'Admin' || user?.role === 'diretor_comercial' || user?.role === 'Diretor Comercial') {
                  return users.map(userItem => (
                    canViewUserCalendar(userItem.id) && userItem.id !== user?.id && (
                      <Option key={userItem.id.toString()} value={userItem.id.toString()}>
                        <UserOutlined /> {userItem.username}
                      </Option>
                    )
                  ));
                }
                
                // For team leaders and other roles, show only team members
                const teamId = selectedTeam || user?.team_id;
                const members = teamId ? getTeamMembers(teamId) : [];
                return canViewTeamMembers && 
                 members.map(member => (
                  canViewUserCalendar(member.id) && member.id !== user?.id && (
                    <Option key={member.id.toString()} value={member.id.toString()}>
                      <UserOutlined /> {member.username}
                    </Option>
                  )
                ));
              })()}
            </Select>
            
            {/* Team Selection - Show if viewing team calendar AND user has team management permissions */}
            {(() => {
              const canManageTeams = user?.role === 'admin' || user?.role === 'Admin' || user?.role === 'diretor_comercial' || user?.role === 'Diretor Comercial' || 
                                   (user?.role === 'team_leader' && user?.team_id) ||
                                   (user?.role === 'gestor_equipa' && user?.team_id) ||
                                   (user?.role === 'gestor_equipa_junior' && user?.team_id);
              const shouldShowTeamSelect = selectedView === 'team' && canManageTeams;
              return shouldShowTeamSelect && (
                <Select
                  placeholder="Selecionar Equipa"
                  value={selectedTeam}
                  onChange={setSelectedTeam}
                  style={{ width: 200 }}
                >
                  {(teams || []).map(team => (
                    <Option key={team.id} value={team.id}>
                      <TeamOutlined /> {team.name}
                    </Option>
                  ))}
                </Select>
              );
            })()}
            
            <Space>
              <Button 
                type="primary" 
                icon={<PlusOutlined />} 
                onClick={handleOpenCreateEventModal}
              >
                Criar Evento
              </Button>
              <Button 
                icon={<SyncOutlined />} 
                onClick={handleSync}
                loading={syncLoading}
                disabled={events.length === 0}
                title={events.length === 0 ? 'Não há eventos para sincronizar' : 'Sincronizar com calendário externo'}
              >
                Sincronizar
              </Button>
            </Space>
          </Space>
        }
      >
        <Row gutter={16}>
          {/* Calendar Legend - Left Side */}
          {(selectedView === 'team' || selectedView !== 'personal') && (
            <Col span={4}>
              <div style={{ 
                padding: '16px', 
                backgroundColor: '#f8f9fa', 
                borderRadius: '8px',
                border: '1px solid #e9ecef',
                height: 'fit-content',
                position: 'sticky',
                top: 16
              }}>
                <Text strong style={{ marginBottom: 12, display: 'block' }}>Legenda:</Text>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <div style={{ 
                      width: 16, 
                      height: 16, 
                      backgroundColor: '#52c41a', 
                      borderRadius: '3px',
                      marginRight: 8,
                      border: '1px solid #389e0d'
                    }}></div>
                    <Text style={{ fontSize: '12px' }}>Meus eventos</Text>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <div style={{ 
                      width: 16, 
                      height: 16, 
                      backgroundColor: '#722ed1', 
                      borderRadius: '3px',
                      marginRight: 8
                    }}></div>
                    <Text style={{ fontSize: '12px' }}>Eventos da equipa</Text>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <div style={{ 
                      width: 16, 
                      height: 16, 
                      backgroundColor: '#1890ff', 
                      borderRadius: '3px',
                      marginRight: 8
                    }}></div>
                    <Text style={{ fontSize: '12px' }}>Outros eventos</Text>
                  </div>
                </div>
              </div>
            </Col>
          )}
          
          {/* Calendar - Right Side */}
          <Col span={selectedView === 'team' || selectedView !== 'personal' ? 20 : 24}>
            <Spin spinning={viewLoading} tip="A carregar calendário...">
              <Calendar
                dateCellRender={dateCellRender}
                onSelect={handleDateSelect}
                style={{ background: 'white' }}
              />
            </Spin>
          </Col>
        </Row>
      </Card>

      <EventModal
        open={eventModalVisible}
        onClose={handleEventModalClose}
        onSuccess={handleEventModalSuccess}
        isEdit={selectedEventForEdit !== null}
        editingEvent={selectedEventForEdit}
      />

      {/* View Events Modal */}
      <Modal
        title={
          <Space>
            <CalendarOutlined />
            Eventos do Dia {selectedDate?.format('DD/MM/YYYY')}
          </Space>
        }
        open={viewModalVisible}
        onCancel={() => setViewModalVisible(false)}
        footer={
          <Space>
            <Button 
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                setViewModalVisible(false);
                handleOpenCreateEventModal();
              }}
            >
              Criar Novo Evento
            </Button>
            <Button onClick={() => setViewModalVisible(false)}>
              Fechar
            </Button>
          </Space>
        }
        width={800}
      >
        {selectedEvents.length === 0 ? (
          <Empty
            description="Nenhum evento para este dia"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          >
            <Button 
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                setViewModalVisible(false);
                handleOpenCreateEventModal();
              }}
            >
              Criar Primeiro Evento
            </Button>
          </Empty>
        ) : (
          <List
            dataSource={selectedEvents}
            renderItem={(event) => (
              <List.Item>
                <Card 
                  title={
                    <Space>
                      {event.title}
                      <Tag>{importanceLevels.find(p => p.value === event.importance_level)?.label}</Tag>
                      <Button
                        type="text"
                        icon={<EditOutlined />}
                        onClick={() => handleEditEvent(event)}
                        style={{ marginLeft: 8 }}
                      >
                        Editar
                      </Button>
                      <Button
                        type="text"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => handleDeleteEvent(event.id)}
                        style={{ marginLeft: 8 }}
                      >
                        Eliminar
                      </Button>
                    </Space>
                  }
                  style={{ width: '100%' }}
                >
                  {getEventDetails(event)}
                </Card>
              </List.Item>
            )}
          />
        )}
      </Modal>

      {/* Calendar Sync Modal */}
      <Modal
        title={
          <Space>
            <SyncOutlined />
            Sincronizar com Calendário Externo
          </Space>
        }
        open={syncModalVisible}
        onCancel={() => setSyncModalVisible(false)}
        footer={null}
        width={1200}
      >
        {syncOptions && (
          <div>
            <Alert
              message="Opções de Sincronização"
              description="Escolha como pretende sincronizar os seus eventos do CRM com o seu calendário pessoal."
              type="info"
              showIcon
              style={{ marginBottom: 24 }}
            />
            
            {/* Filters Section */}
            <Card title="Filtros" size="small" style={{ marginBottom: 16 }}>
              <Row gutter={16}>
                <Col span={6}>
                  <Input
                    placeholder="Pesquisar eventos..."
                    value={eventFilters.search}
                    onChange={(e) => setEventFilters({ ...eventFilters, search: e.target.value })}
                    prefix={<SearchOutlined />}
                  />
                </Col>
                <Col span={6}>
                  <RangePicker
                    placeholder={['Data Início', 'Data Fim']}
                    value={eventFilters.dateRange}
                    onChange={(dates) => setEventFilters({ ...eventFilters, dateRange: dates as [Moment, Moment] | null })}
                    style={{ width: '100%' }}
                  />
                </Col>
                <Col span={4}>
                  <Select
                    placeholder="Importância"
                    value={eventFilters.importance}
                    onChange={(value) => setEventFilters({ ...eventFilters, importance: value })}
                    allowClear
                    style={{ width: '100%' }}
                  >
                    {importanceLevels.map(level => (
                      <Option key={level.value} value={level.value}>
                        {level.label}
                      </Option>
                    ))}
                  </Select>
                </Col>
                <Col span={4}>
                  <Select
                    placeholder="Tipo"
                    value={eventFilters.type}
                    onChange={(value) => setEventFilters({ ...eventFilters, type: value })}
                    allowClear
                    style={{ width: '100%' }}
                  >
                    {eventTypes.map(type => (
                      <Option key={type.value} value={type.value}>
                        {type.label}
                      </Option>
                    ))}
                  </Select>
                </Col>
                <Col span={4}>
                  <Button
                    onClick={() => setEventFilters({ search: '', dateRange: null, importance: null, type: null })}
                    icon={<ClearOutlined />}
                  >
                    Limpar
                  </Button>
                </Col>
              </Row>
            </Card>

            {/* Events Table */}
            <Card title={`Eventos Disponíveis (${getFilteredAndPaginatedEvents().total} encontrados)`} size="small">
              <Table
                dataSource={getFilteredAndPaginatedEvents().events}
                columns={[
                  {
                    title: 'Título',
                    dataIndex: 'title',
                    key: 'title',
                    width: 200,
                    render: (text, record) => (
                      <div>
                        <div style={{ fontWeight: 'bold' }}>{text}</div>
                        {record.description && (
                          <div style={{ fontSize: '12px', color: '#666' }}>
                            {record.description.length > 50 ? `${record.description.substring(0, 50)}...` : record.description}
                          </div>
                        )}
                      </div>
                    )
                  },
                  {
                    title: 'Data/Hora',
                    dataIndex: 'date',
                    key: 'date',
                    width: 120,
                    sorter: (a, b) => moment(a.date, 'DD/MM/YYYY HH:mm').unix() - moment(b.date, 'DD/MM/YYYY HH:mm').unix()
                  },
                  {
                    title: 'Importância',
                    dataIndex: 'importance',
                    key: 'importance',
                    width: 100,
                    render: (value) => {
                      const level = importanceLevels.find(l => l.value === value);
                      return level ? <Tag color={value === 3 ? 'red' : value === 2 ? 'orange' : 'green'}>{level.label}</Tag> : '-';
                    }
                  },
                  {
                    title: 'Tipo',
                    dataIndex: 'type',
                    key: 'type',
                    width: 100,
                    render: (value) => {
                      const type = eventTypes.find(t => t.value === value);
                      return type ? type.label : value;
                    }
                  },
                  {
                    title: 'Ações',
                    key: 'actions',
                    width: 300,
                    render: (_, record) => (
                      <Space>
                        <Button
                          size="small"
                          icon={<DownloadOutlined />}
                          onClick={handleDownloadIcal}
                          title="Download iCal"
                        >
                          iCal
                        </Button>
                        <Button
                          size="small"
                          type="primary"
                          icon={<LinkOutlined />}
                          onClick={() => handleOpenEventInCalendar(record, 'google_calendar')}
                          title="Google Calendar"
                        >
                          Google
                        </Button>
                        <Button
                          size="small"
                          type="primary"
                          icon={<LinkOutlined />}
                          onClick={() => handleOpenEventInCalendar(record, 'outlook_calendar')}
                          title="Outlook Calendar"
                        >
                          Outlook
                        </Button>
                        <Button
                          size="small"
                          type="primary"
                          icon={<LinkOutlined />}
                          onClick={() => handleOpenEventInCalendar(record, 'apple_calendar')}
                          title="Apple Calendar"
                        >
                          Apple
                        </Button>
                      </Space>
                    )
                  }
                ]}
                pagination={{
                  current: currentPage,
                  pageSize: pageSize,
                  total: getFilteredAndPaginatedEvents().total,
                  showSizeChanger: true,
                  showQuickJumper: true,
                  showTotal: (total, range) => `${range[0]}-${range[1]} de ${total} eventos`,
                  onChange: (page, size) => {
                    setCurrentPage(page);
                    setPageSize(size || 10);
                  }
                }}
                size="small"
                scroll={{ x: 800 }}
              />
            </Card>

            {/* Quick Actions */}
            <Card title="Ações Rápidas" size="small" style={{ marginTop: 16 }}>
              <Row gutter={16} style={{ marginBottom: 16 }}>
                <Col span={6}>
                  <Button
                    type="primary"
                    icon={<DownloadOutlined />}
                    onClick={handleDownloadIcal}
                    block
                  >
                    Download iCal (Todos)
                  </Button>
                </Col>
                <Col span={6}>
                  <Button
                    type="primary"
                    icon={<LinkOutlined />}
                    onClick={() => handleOpenMultipleEvents(syncOptions.google_calendar.urls, 'Google Calendar')}
                    block
                  >
                    Google Calendar
                  </Button>
                </Col>
                <Col span={6}>
                  <Button
                    type="primary"
                    icon={<LinkOutlined />}
                    onClick={() => handleOpenMultipleEvents(syncOptions.outlook_calendar.urls, 'Outlook Calendar')}
                    block
                  >
                    Outlook Calendar
                  </Button>
                </Col>
                <Col span={6}>
                  <Button
                    type="primary"
                    icon={<LinkOutlined />}
                    onClick={() => handleOpenMultipleEvents(syncOptions.apple_calendar.urls, 'Apple Calendar')}
                    block
                  >
                    Apple Calendar
                  </Button>
                </Col>
              </Row>
              <Row gutter={16}>
                <Col span={24}>
                  <Button
                    type="default"
                    icon={<LinkOutlined />}
                    onClick={() => handleOpenExternalCalendar(syncOptions.webcal_subscription.url, 'subscription')}
                    block
                  >
                    Subscrição Manual
                  </Button>
                </Col>
              </Row>
            </Card>
            
            <Divider />
            
            <Alert
              message="Dicas de Sincronização"
              description={
                <ul style={{ margin: 0, paddingLeft: 20 }}>
                  <li><strong>Filtros:</strong> Use os filtros acima para encontrar eventos específicos</li>
                  <li><strong>iCal Download:</strong> Descarregue um ficheiro .ics com todos os eventos filtrados</li>
                  <li><strong>Calendários Web:</strong> Clique em cada evento individualmente ou use as ações rápidas</li>
                  <li><strong>Subscrição Manual:</strong> Use o URL de subscrição para sincronização contínua</li>
                  <li><strong>Apple Calendar:</strong> Suporte completo com descarregamento de ficheiros .ics</li>
                  <li><strong>Prevenção de Duplicados:</strong> Cada evento tem um identificador único</li>
                </ul>
              }
              type="success"
              showIcon
            />
          </div>
        )}
      </Modal>
    </div>
  );
};

export default CalendarComponent; 